import { useCallback, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { ListChannels } from '@/modules/connect/use-cases'
import type { LoadHuntTypesWithActivity } from '@/shared/domain'
import { channelsKey, huntTypeActivityKey } from '@/shared/infra/query'
import { useOrgContext } from '@/shared/ui/hooks'
import { NavPrefetchContext } from './nav-prefetch-context'

const PREFETCHABLE_NAV_IDS = new Set([
  'dashboard',
  'hunt-logs',
  'analytics',
  'connect-messages',
])

type NavPrefetchProviderProps = {
  onListChannels: ListChannels
  onLoadHuntTypesWithActivity: LoadHuntTypesWithActivity
  children: ReactNode
}

export function NavPrefetchProvider({
  onListChannels,
  onLoadHuntTypesWithActivity,
  children,
}: NavPrefetchProviderProps) {
  const queryClient = useQueryClient()
  const { orgId, orgType } = useOrgContext()

  const prefetch = useCallback(
    (navId: string) => {
      if (!PREFETCHABLE_NAV_IDS.has(navId)) return
      if (!orgId || (orgType !== 'club' && orgType !== 'outfitter')) return
      const resolvedOrgType = orgType

      if (navId === 'connect-messages') {
        void queryClient.prefetchQuery({
          queryKey: channelsKey(orgId, resolvedOrgType),
          queryFn: () => onListChannels({ orgId, orgType: resolvedOrgType }),
        })
        return
      }

      void queryClient.prefetchQuery({
        queryKey: huntTypeActivityKey(orgId, resolvedOrgType),
        queryFn: () =>
          onLoadHuntTypesWithActivity({ orgContext: { orgId, orgType: resolvedOrgType } }),
      })
    },
    [queryClient, orgId, orgType, onListChannels, onLoadHuntTypesWithActivity],
  )

  const value = useMemo(() => ({ prefetch }), [prefetch])

  return <NavPrefetchContext.Provider value={value}>{children}</NavPrefetchContext.Provider>
}
